import React, { useState, useCallback, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  Badge,
  Slide,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChatBox from './ChatBox';
import { getConversations, getAvailableStudents } from '../../services/chatService';
import { SocketContext } from '../../contexts/SocketContext';
import { STATUS_COLORS } from '../../config/constants';
import { formatDistanceToNow } from 'date-fns';

const FloatingChat = () => {
  const { socket } = useContext(SocketContext);
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);

  // Active chat state
  const [activeStudentId, setActiveStudentId] = useState(null);
  const [activeStudentName, setActiveStudentName] = useState('');

  // New chat search panel
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);

  // Online presence
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());

  // Listen for online presence events
  useEffect(() => {
    if (!socket) return;

    socket.emit('get_online_users');

    const handleOnlineUsers = (users) => {
      setOnlineUserIds(new Set(users.map((u) => u.userId)));
    };
    const handleUserOnline = ({ userId }) => {
      setOnlineUserIds((prev) => new Set([...prev, userId]));
    };
    const handleUserOffline = ({ userId }) => {
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    };

    socket.on('online_users', handleOnlineUsers);
    socket.on('user_online', handleUserOnline);
    socket.on('user_offline', handleUserOffline);

    return () => {
      socket.off('online_users', handleOnlineUsers);
      socket.off('user_online', handleUserOnline);
      socket.off('user_offline', handleUserOffline);
    };
  }, [socket]);

  // Fetch conversations when panel opens
  const fetchConversations = useCallback(async () => {
    setLoadingConversations(true);
    try {
      const { data } = await getConversations();
      setConversations(data.data?.conversations || []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  // Debounced search for available students
  useEffect(() => {
    if (!showNewChat) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!searchQuery.trim()) {
      searchTimerRef.current = setTimeout(async () => {
        setSearchLoading(true);
        try {
          const { data } = await getAvailableStudents('');
          setSearchResults(data.data?.students || []);
        } catch (err) {
          console.error('Failed to fetch students:', err);
        } finally {
          setSearchLoading(false);
        }
      }, 100);
      return;
    }

    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await getAvailableStudents(searchQuery);
        setSearchResults(data.data?.students || []);
      } catch (err) {
        console.error('Failed to search students:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQuery, showNewChat]);

  const handleOpen = () => {
    setActiveStudentId(null);
    setActiveStudentName('');
    setShowNewChat(false);
    fetchConversations();
    setOpen(true);
  };

  const handleSelectConversation = (conv) => {
    setActiveStudentId(conv.studentId);
    setActiveStudentName(conv.studentName);
    setShowNewChat(false);
  };

  const handleSelectStudent = (student) => {
    setActiveStudentId(student._id);
    setActiveStudentName(student.name);
    setShowNewChat(false);
  };

  const handleBack = () => {
    if (showNewChat) {
      setShowNewChat(false);
      setSearchQuery('');
      setSearchResults([]);
      return;
    }
    setActiveStudentId(null);
    setActiveStudentName('');
    fetchConversations();
  };

  const handleClose = () => {
    setOpen(false);
    setActiveStudentId(null);
    setActiveStudentName('');
    setShowNewChat(false);
    setSearchQuery('');
  };

  const handleNewChat = () => {
    setShowNewChat(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const showBackButton = !!activeStudentId || showNewChat;

  const getStatusColor = (status) => STATUS_COLORS[status] || '#94A3B8';

  return (
    <>
      {/* Floating chat panel */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: { xs: 'calc(100% - 48px)', sm: 390 },
            height: 540,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid #E2E8F0',
            boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              background: 'linear-gradient(135deg, #1A73E8 0%, #1557B0 100%)',
              color: '#fff',
              minHeight: 52,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showBackButton && (
                <IconButton size="small" onClick={handleBack} sx={{ color: '#fff' }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              )}
              <ChatIcon fontSize="small" sx={{ opacity: 0.9 }} />
              <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ letterSpacing: '-0.01em' }}>
                {activeStudentId
                  ? `${activeStudentName || 'Chat'}`
                  : showNewChat
                  ? 'New Conversation'
                  : 'Messages'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {!activeStudentId && !showNewChat && (
                <IconButton size="small" onClick={handleNewChat} sx={{ color: '#fff' }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'hidden', backgroundColor: '#FFFFFF' }}>
            {activeStudentId ? (
              <ChatBox studentId={activeStudentId} />
            ) : showNewChat ? (
              /* New Chat - Student Search Panel */
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by student name or file number..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        fontSize: '0.85rem',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1A73E8',
                        },
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {searchLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ color: '#1A73E8' }} />
                    </Box>
                  ) : searchResults.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, px: 3 }}>
                      <SchoolOutlinedIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#5F6368', textAlign: 'center' }}>
                        {searchQuery ? 'No students found' : 'Search for a student to start chatting'}
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {searchResults.map((student, idx) => (
                        <React.Fragment key={student._id}>
                          <ListItemButton
                            onClick={() => handleSelectStudent(student)}
                            sx={{
                              py: 1.25,
                              px: 2,
                              '&:hover': { backgroundColor: '#F8FAFC' },
                            }}
                          >
                            <ListItemAvatar sx={{ minWidth: 40 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  backgroundColor: 'rgba(26, 115, 232, 0.1)',
                                  color: '#1A73E8',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {student.name?.[0]?.toUpperCase() || 'S'}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight={500} sx={{ color: '#202124', fontSize: '0.85rem' }}>
                                  {student.name}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                  <Typography variant="caption" sx={{ color: '#5F6368', fontSize: '0.72rem' }}>
                                    {student.fileNumber}
                                  </Typography>
                                  {student.status && (
                                    <Chip
                                      label={student.status}
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        backgroundColor: `${getStatusColor(student.status)}14`,
                                        color: getStatusColor(student.status),
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  )}
                                </Box>
                              }
                            />
                          </ListItemButton>
                          {idx < searchResults.length - 1 && <Divider component="li" sx={{ borderColor: '#F1F5F9' }} />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              </Box>
            ) : (
              /* Conversation List */
              <Box sx={{ height: '100%', overflowY: 'auto' }}>
                {loadingConversations ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: '#1A73E8' }} />
                  </Box>
                ) : conversations.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, px: 3 }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1.5 }} />
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#202124', mb: 0.5 }}>
                      No conversations yet
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#5F6368', textAlign: 'center', mb: 2 }}>
                      Start a conversation by clicking the + button above
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {conversations.map((conv, idx) => (
                      <React.Fragment key={conv.studentId}>
                        <ListItemButton
                          onClick={() => handleSelectConversation(conv)}
                          sx={{
                            py: 1.25,
                            px: 2,
                            '&:hover': { backgroundColor: '#F8FAFC' },
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 44 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                backgroundColor: conv.unreadCount > 0 ? '#1A73E8' : 'rgba(26, 115, 232, 0.1)',
                                color: conv.unreadCount > 0 ? '#fff' : '#1A73E8',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                              }}
                            >
                              {conv.studentName?.[0]?.toUpperCase() || 'S'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={conv.unreadCount > 0 ? 700 : 500}
                                  noWrap
                                  sx={{ maxWidth: 180, color: '#202124', fontSize: '0.85rem' }}
                                >
                                  {conv.studentName}
                                </Typography>
                                {conv.unreadCount > 0 && (
                                  <Chip
                                    label={conv.unreadCount}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      minWidth: 20,
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      backgroundColor: '#F43F5E',
                                      color: '#fff',
                                      '& .MuiChip-label': { px: 0.5 },
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography
                                  variant="caption"
                                  noWrap
                                  component="div"
                                  sx={{
                                    maxWidth: 250,
                                    color: conv.unreadCount > 0 ? '#202124' : '#5F6368',
                                    fontWeight: conv.unreadCount > 0 ? 500 : 400,
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {conv.senderName}: {conv.lastMessage || 'Attachment'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                  <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.68rem' }}>
                                    {conv.fileNumber}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.68rem' }}>
                                    &middot;
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.68rem' }}>
                                    {conv.lastMessageAt
                                      ? formatDistanceToNow(new Date(conv.lastMessageAt), { addSuffix: true })
                                      : ''}
                                  </Typography>
                                  {conv.status && (
                                    <>
                                      <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.68rem' }}>
                                        &middot;
                                      </Typography>
                                      <Chip
                                        label={conv.status}
                                        size="small"
                                        sx={{
                                          height: 16,
                                          fontSize: '0.58rem',
                                          fontWeight: 600,
                                          backgroundColor: `${getStatusColor(conv.status)}14`,
                                          color: getStatusColor(conv.status),
                                          '& .MuiChip-label': { px: 0.5 },
                                        }}
                                      />
                                    </>
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                        {idx < conversations.length - 1 && <Divider component="li" sx={{ borderColor: '#F1F5F9' }} />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Slide>

      {/* Floating action button */}
      {!open && (
        <Fab
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            backgroundColor: '#1A73E8',
            color: '#fff',
            boxShadow: '0 4px 14px rgba(26, 115, 232, 0.4)',
            '&:hover': {
              backgroundColor: '#1557B0',
              boxShadow: '0 6px 20px rgba(26, 115, 232, 0.5)',
            },
          }}
          aria-label="Open chat"
        >
          <Badge
            badgeContent={totalUnread}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#F43F5E',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.7rem',
              },
            }}
          >
            <ChatIcon />
          </Badge>
        </Fab>
      )}
    </>
  );
};

export default FloatingChat;
